import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Table, Checkbox, Input, Button } from 'semantic-ui-react';
import { parseLoomId } from '../../pagesv2/TrainingPlan/trainingPlan';

// Per-company Training Plan Looms editable here (Multiply admins only — the
// field is in patchHandler's ADMIN_ONLY_FIELDS). Accepts a share/embed URL or
// a bare id; only the id is stored, so the customer-facing iframe can never be
// pointed at an arbitrary URL. Swapping a video is a data edit, not a deploy.
const VIDEO_SLOTS = [
    { key: 'intro', placeholder: 'Intro Loom (URL or id)' },
    { key: 'elevatorPitch', placeholder: 'Elevator Pitch example' },
    { key: 'casualEncounter', placeholder: 'Casual Encounter example' },
];

class CompaniesGrid extends Component {
    state = { loomDrafts: {}, loomSavingId: null, loomErrorId: null };

    // After toggling a feature flag, the edited Company is a separate MST
    // instance from companyStore.company (the "current company" the router
    // reads). If we just edited the current company, re-fetch it so gated
    // routes/UI reflect the change without a re-login or company switch.
    syncCurrentCompany = async (companyId) => {
        const { companyStore } = this.props;
        if (companyStore?.company?.id && companyStore.company.id === companyId) {
            await companyStore.loadCompany(companyId);
        }
    }

    draftKey = (companyId, slotKey) => `${companyId}:${slotKey}`;

    loomValue = (company, slotKey) => {
        const key = this.draftKey(company.id, slotKey);
        if (key in this.state.loomDrafts) return this.state.loomDrafts[key];
        return company.trainingPlanVideos?.[slotKey]?.loomId || '';
    };

    loomsDirty = (company) => VIDEO_SLOTS.some(({ key }) => {
        const draftKey = this.draftKey(company.id, key);
        return draftKey in this.state.loomDrafts &&
            this.state.loomDrafts[draftKey] !== (company.trainingPlanVideos?.[key]?.loomId || '');
    });

    setLoomDraft = (company, slotKey, value) => {
        this.setState((prev) => ({
            loomDrafts: { ...prev.loomDrafts, [this.draftKey(company.id, slotKey)]: value },
            loomErrorId: prev.loomErrorId === company.id ? null : prev.loomErrorId,
        }));
    };

    saveLooms = async (company) => {
        const videos = {};
        for (const { key } of VIDEO_SLOTS) {
            const raw = this.loomValue(company, key).trim();
            if (!raw) continue; // empty input = remove the slot
            const loomId = parseLoomId(raw);
            if (!loomId) {
                this.setState({ loomErrorId: company.id });
                return;
            }
            videos[key] = { ...(company.trainingPlanVideos?.[key] || {}), loomId };
        }
        this.setState({ loomSavingId: company.id, loomErrorId: null });
        try {
            await company.updateTrainingPlanVideos(videos);
            await this.syncCurrentCompany(company.id);
            this.setState((prev) => {
                const loomDrafts = { ...prev.loomDrafts };
                VIDEO_SLOTS.forEach(({ key }) => delete loomDrafts[this.draftKey(company.id, key)]);
                return { loomDrafts };
            });
        } catch (error) {
            console.error('Failed to save Training Plan Looms:', error);
        } finally {
            this.setState({ loomSavingId: null });
        }
    };

    renderLoomCell(company) {
        return (
            <div style={{ minWidth: '190px' }}>
                {VIDEO_SLOTS.map(({ key, placeholder }) => (
                    <Input
                        key={key}
                        size="mini"
                        fluid
                        placeholder={placeholder}
                        value={this.loomValue(company, key)}
                        onChange={(e) => this.setLoomDraft(company, key, e.target.value)}
                        style={{ marginBottom: '4px' }}
                    />
                ))}
                {this.state.loomErrorId === company.id && (
                    <div style={{ color: '#dc3545', fontSize: '11px', marginBottom: '4px' }}>
                        Couldn't read a Loom id from one of the values. Paste the share URL or the bare id.
                    </div>
                )}
                {this.loomsDirty(company) && (
                    <Button
                        size="mini"
                        primary
                        loading={this.state.loomSavingId === company.id}
                        onClick={() => this.saveLooms(company)}
                    >
                        Save Looms
                    </Button>
                )}
            </div>
        );
    }

    renderTableRows(adminCompanies) {
        return adminCompanies.map((adminCompany, index) => <Table.Row key={`company-${index}`}>
            <Table.Cell>
                {adminCompany.company.name}
            </Table.Cell>
            <Table.Cell>
                {adminCompany.companyAdmin?.email}
            </Table.Cell>
            <Table.Cell>
                {adminCompany.totalUsersCount}
            </Table.Cell>
            <Table.Cell>
                {adminCompany.activeUsersCount}
            </Table.Cell>
            <Table.Cell>
                <Checkbox
                    toggle
                    checked={adminCompany.company.investorPitchEnabled}
                    onChange={async () => {
                        await adminCompany.company.updateInvestorPitchEnabled(
                            !adminCompany.company.investorPitchEnabled
                        );
                        await this.syncCurrentCompany(adminCompany.company.id);
                    }}
                />
            </Table.Cell>
            <Table.Cell>
                <Checkbox
                    toggle
                    checked={adminCompany.company.liveMeetingsEnabled}
                    onChange={async () => {
                        await adminCompany.company.updateLiveMeetingsEnabled(
                            !adminCompany.company.liveMeetingsEnabled
                        );
                        await this.syncCurrentCompany(adminCompany.company.id);
                    }}
                />
            </Table.Cell>
            <Table.Cell>
                {this.renderLoomCell(adminCompany.company)}
            </Table.Cell>
        </Table.Row>);
    }

    render() {
        const { adminCompanies } = this.props;

        return (
            <Table striped>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            Company Name
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            Company Admin Email
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            Total Users
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            Active Users
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            Investor Pitch
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            Live Meetings
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            Training Plan Looms
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {this.renderTableRows(adminCompanies)}
                </Table.Body>
            </Table>
        );
    }
}

export default observer(CompaniesGrid);
